import { Component, QueryList, ContentChildren, AfterContentInit, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

import * as fromRootStore from '@apps/root/store';
import { IAuroraState } from '@apps/aurora.state';

import { StepComponent } from './step/step.component';
import { NextParams, StepState } from './models';

@Component({
    selector: 'aa-wizard2',
    templateUrl: './wizard2.component.html',
    styleUrls: [
        './wizard.component.scss'
    ],
    standalone: false
})
export class Wizard2Component implements OnInit, AfterContentInit, OnDestroy
{
    protected destroyator = new Subject();
    @ContentChildren(StepComponent) steps: QueryList<StepComponent>;
    @Input() justified: boolean = false;
    @Input() height: number = 50;
    @Input() nextWizardStep: () => any;
    @Output() selectedStep: EventEmitter<number> = new EventEmitter<number>();
    public height1: number;
    public activeId: number = 0;
    public nextDisabled: boolean = true;

    constructor (
        private store: Store<IAuroraState>,
        private actions: Actions
    )
    { }

    ngOnInit ()
    {
        this.height1 = this.height;
    }

    ngAfterContentInit ()
    {
        this.steps.forEach((step, idx) =>
        {
            step.stepId = idx;
        });

        const activeSteps: StepComponent[] = this.steps
            .filter((step: StepComponent) => step.active);

        if (activeSteps.length === 0)
        {
            this.activateStep(this.steps.first.stepId);
        }

        this.actions
            .pipe(
                ofType(fromRootStore.RootMiscStoreActions.RootWizard2StepState),
                tap((stepState: StepState) =>
                {
                    Promise.resolve(null).then(() =>
                    {
                        const step = this.steps
                            .find((s) =>
                                s.stepId === stepState.stepIdx
                            );
                        step.valid = stepState.valid;
                        this.nextDisabled = !(this.activeId !== (this.steps.length - 1) && stepState.valid);
                    });
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();

        this.actions
            .pipe(
                ofType(fromRootStore.RootMiscStoreActions.RootWizard2StepEnabled),
                tap((params: { stepIdx: number, deactivated: boolean }) =>
                {
                    const step = this.steps
                        .find((step: StepComponent) =>
                            step.stepId === params.stepIdx
                        );
                    step.deactivated = params.deactivated;
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();

        this.actions
            .pipe(
                ofType(fromRootStore.RootMiscStoreActions.RootWizard2StepExitAccepted),
                tap((params: { oldStepIdx: number, newStepIdx: number }) =>
                {
                    this.activateStep(params.newStepIdx);
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();

        this.actions
            .pipe(
                ofType(fromRootStore.RootMiscStoreActions.RootWizard2NextStepButtonClicked),
                tap(() =>
                    this.onClickNextStep()
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();

        this.actions
            .pipe(
                ofType(fromRootStore.RootMiscStoreActions.RootWizard2NextButtonClicked),
                tap(() =>
                    this.onClickNextStep()
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();

        this.actions
            .pipe(
                ofType(fromRootStore.RootMiscStoreActions.RootWizardStepActivated),
                tap(({ stepIdx }: { stepIdx: number }) =>
                {
                    this.activateStep(stepIdx);
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngOnDestroy (): void
    {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    /**
     *
     * @param {number} idx Index de l'onglet qui a été cliqué
     */
    public onClickStep (idx: number)
    {
        this.nextStep(idx);
    }

    /**
     * Méthode activée lors du click sur l'icône 'Next Step' au niveau des onglets
     */
    public onClickNextStep (): void
    {
        this.nextStep(this.nextEnabledStep(this.activeId));
    }

    private nextStep (newStepIdx: number)
    {
        const nextParams: NextParams = new NextParams(this.activeId, newStepIdx);

        this.store.dispatch(
            fromRootStore.RootMiscStoreActions.RootWizard2StepExitRequested(nextParams)
        );
    }

    private activateStep (stepIdx: number): void
    {
        Promise.resolve(null).then(() =>
        {
            const step = this.steps.find((item: StepComponent) =>
                item.stepId === stepIdx
            );

            step.disabled = false;

            this.steps.forEach((item: StepComponent) =>
                item.active = false
            );
            step.active = true;
            this.activeId = step.stepId;
            if (this.activeId === (this.steps.length - 1))
            {
                this.nextDisabled = true;
            }

            this.selectedStep.emit(stepIdx);
        });
    }

    private nextEnabledStep (fromIdx: number): number
    {
        const steps = this.steps.toArray()
            .filter((step: StepComponent) =>
                !step.deactivated
            );

        let nextIdx: number = steps
            .findIndex((step: StepComponent) =>
                step.stepId > fromIdx
            );

        if (nextIdx === - 1)
        {
            nextIdx = steps
                .findIndex((step: StepComponent) =>
                    step.stepId < fromIdx
                );
        }

        return steps[nextIdx].stepId;
    }
}
